import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import i18n from "../../../language/i18n";
import { useAuth } from "../../../context/UserContextProvider";
import {
  ApproveButton,
  BaseLoading,
  CancelButton,
  EmptyState,
  Icon,
  PrimaryButton,
  SecondaryButton,
  BaseModal as _BaseModal,
} from "../../../library";
import MascotWithItems from "../../common/mascots/MascotWithItems";
import DuelCreateForm from "./DuelCreateForm";
import {
  createDuel,
  retrieveActiveDuel,
  retrieveOnlinePeers,
  inviteToDuel,
} from "../../../requests/CatchtivityRequests";
import {
  acceptFriendRequest,
  declineFriendRequest,
  deleteFriendship,
  retrieveFriendList,
  retrievePendingFriendRequests,
  searchFriendCandidates,
  sendFriendRequest,
} from "../../../requests/ManagementRequests";
import { NotificationManager } from "../../../notifications/NotificationManager";
import { duelSocialStyles, friendPanelStyles } from "./duelSocialStyles";
import {
  FRIEND_EVENT,
  FriendCandidate,
  FriendPeer,
  PendingFriendships,
  PresenceStatus,
  isInterruptible,
} from "./friendTypes";
import { resolveDuelInviteRefusalKey } from "./duelInviteRefusal";

const BaseModal: any = _BaseModal;
const POLL_INTERVAL_MS = 15000;
// How long without a mouse/key/scroll/click before the roster poll parks itself. Deliberately far
// SHORTER than the websocket's 30-minute idle reaper: the socket is a single cheap connection, this
// is four HTTP requests every 15 seconds (presence, roster, pending requests, active duel).
const POLL_IDLE_MS = 120000;
const SEARCH_DEBOUNCE_MS = 400;

type PanelTab = "FRIENDS" | "REQUESTS" | "ADD";

// Each status gets a glyph + label; the design system's <Icon> only, never an emoji (lint:no-emoji-icon).
const STATUS_PRESENTATION: Record<
  PresenceStatus,
  { icon: string; labelKey: string }
> = {
  ONLINE: { icon: "circleFill", labelKey: "status_online" },
  SOLVING: { icon: "pencil", labelKey: "status_solving" },
  IN_DUEL: { icon: "sword", labelKey: "status_in_duel" },
  IN_EXAM: { icon: "exam", labelKey: "status_in_exam" },
  OFFLINE: { icon: "circle", labelKey: "status_offline" },
};

// The friends drawer, in three tabs:
//  - FRIENDS  → accepted friends with live presence; only an idle ONLINE friend can be pulled into a duel.
//  - REQUESTS → incoming (accept / decline) and outgoing (cancel) friend requests.
//  - ADD      → search the caller's own season and send a request.
//
// The FRIENDS roster is management's accepted-friend list; catchtivity's /online-peers supplies presence and
// mascots, joined onto that roster by profile id (see friendRows). The join direction is the security
// boundary, not a convenience: a presence row without a matching friendship is dropped, so a non-friend is
// never rendered even if the deployed catchtivity still returns the whole season roster (pre-#621).
// Row actions in the FRIENDS tab adapt to the caller's current duel:
//  - no active duel   → "Meydan oku" opens the create-game modal, then creates a duel + invites.
//  - active, slot open → "Davet et" invites the friend into the open opponent slot.
//  - active, full      → "İzlemeye davet et" invites them to watch.
// Prefix: dso-.
const FriendsOnlinePanel = (props: { onClose: () => void }) => {
  const { onClose } = props;
  const navigate = useNavigate();
  const { userInformation, userProfile, userProfileSeason, userProfileGrade } =
    useAuth();

  const [tab, setTab] = useState<PanelTab>("FRIENDS");
  // Presence rows from catchtivity. NOT the roster — see friendList below.
  // Last mouse/key/scroll/click, for the poll gate. A ref, not state: it is read inside the interval
  // and must never itself cause a render.
  const lastActivityAtRef = useRef<number>(Date.now());
  // When the tab went hidden, so the return handler can tell "away long enough to miss a tick" from
  // "alt-tabbed and came straight back". `document.hidden` parks the tick on its own, independently
  // of idleness, so the idle clock alone cannot answer that.
  const hiddenSinceRef = useRef<number | null>(null);
  const [peers, setPeers] = useState<FriendPeer[]>([]);
  // Management's accepted-friend list, which IS the roster. Friendship is management's to own, and this is
  // the only source that can answer "is this person actually my friend" without depending on which
  // catchtivity build is deployed: before catchupper-parent#621, /online-peers returns the whole season
  // roster. Rendering presence rows directly would therefore show non-friends and leak their live status on
  // any pre-#621 backend, so the two are intersected here rather than trusted apart.
  const [friendList, setFriendList] = useState<FriendCandidate[]>([]);
  // Whether the roster fetch has ever come back. Until it has there is nothing to intersect against, and
  // falling back to the raw presence rows would be the leak — so this gates rendering and fails CLOSED.
  const [friendListResolved, setFriendListResolved] = useState(false);
  const [pending, setPending] = useState<PendingFriendships>({
    incomingList: [],
    outgoingList: [],
  });
  const [candidates, setCandidates] = useState<FriendCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [activeDuel, setActiveDuel] = useState<any>(null);
  // Until the first active-duel fetch resolves, activeDuel is null — which looks identical to
  // "no active duel". Track resolution so we don't route an intended invite-to-existing-duel
  // through the heavier createDuel "Challenge" path during that window.
  const [activeDuelResolved, setActiveDuelResolved] = useState(false);
  // In-flight row actions, keyed per profile: one learner's round-trip must not disable every other row
  // across all three tabs (accepting one request used to block accepting the next until it returned).
  // The ref is the guard authority because it updates synchronously — a second click in the same tick has
  // to lose, which a state read from the render closure cannot guarantee. The list mirrors it for `disabled`.
  const pendingProfileIdSetRef = useRef<Set<number>>(new Set());
  const [pendingProfileIdList, setPendingProfileIdList] = useState<number[]>([]);
  const isRowPending = (profileId: number) =>
    pendingProfileIdList.includes(profileId);
  /** Claims the row. Returns false when it is already in flight, so callers can just bail. */
  const beginRowPending = (profileId: number) => {
    if (pendingProfileIdSetRef.current.has(profileId)) return false;
    pendingProfileIdSetRef.current.add(profileId);
    setPendingProfileIdList(Array.from(pendingProfileIdSetRef.current));
    return true;
  };
  const endRowPending = (profileId: number) => {
    pendingProfileIdSetRef.current.delete(profileId);
    setPendingProfileIdList(Array.from(pendingProfileIdSetRef.current));
  };
  const [challengePeerId, setChallengePeerId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [unfriendPeer, setUnfriendPeer] = useState<FriendPeer | null>(null);

  const retrieveAndSetOnlinePeers = useCallback(async () => {
    const { data, err } = await retrieveOnlinePeers();
    if (!err && Array.isArray(data)) {
      setPeers(data as FriendPeer[]);
    }
    setLoading(false);
  }, []);

  // The accepted-friend roster (and the friendship ids the presence projection deliberately omits). This is
  // polled alongside presence rather than only fetched on open + friend events: now that it decides WHO is
  // rendered, a missed friend push would otherwise hide a real friend until the panel was reopened, where
  // before it only cost the unfriend affordance. The poll is the documented recovery path for a dropped
  // push, so the roster has to be on it too.
  const retrieveAndSetFriendList = useCallback(async () => {
    const { data, err } = await retrieveFriendList();
    // A failed refresh keeps the last known roster instead of emptying it — a transient management blip
    // must not blank a working panel. Only the never-resolved case renders nothing (see friendListResolved).
    if (err || !Array.isArray(data)) return;
    setFriendList(data as FriendCandidate[]);
    setFriendListResolved(true);
  }, []);

  const retrieveAndSetPending = useCallback(async () => {
    const { data, err } = await retrievePendingFriendRequests();
    if (err || !data) return;
    setPending({
      incomingList: (data as PendingFriendships).incomingList || [],
      outgoingList: (data as PendingFriendships).outgoingList || [],
    });
  }, []);

  const refreshActiveDuel = useCallback(async () => {
    const { data, status } = await retrieveActiveDuel(userProfile.id);
    setActiveDuel(status === 200 && data && (data as any).id ? data : null);
    setActiveDuelResolved(true);
  }, [userProfile.id]);

  useEffect(() => {
    retrieveAndSetOnlinePeers();
    retrieveAndSetFriendList();
    retrieveAndSetPending();
    refreshActiveDuel();
    // Poll the roster, presence and the active duel together — the row action (challenge / invite-to-slot /
    // invite-to-watch) derives from the duel and must not go stale when it fills or ends while the
    // drawer stays open. This is also the recovery path for a missed status or friendship push, which is why
    // the roster is polled here and not only on open.
    //
    // GATED on the tab being visible AND the learner still being present. Ungated, a drawer left open
    // on a backgrounded tab kept firing three requests every 15 seconds — 12 a minute, per learner,
    // indefinitely — for a panel nobody was looking at.
    //
    // "Present" has to include READING, not just clicking: this is the recovery path for a missed
    // status or friendship push, and watching the roster for a friend to appear is exactly the case
    // with no clicks in it. The listener below therefore tracks pointer movement and captures inner
    // scrollports — see the note there; a gate that only saw clicks would freeze the panel under the
    // one learner most likely to notice.
    //
    // Parking costs nothing when it is right to park: the effect below re-runs the fetches the moment
    // presence or visibility returns, so the roster is fresh again before it can be read.
    const tick = () => {
      if (document.hidden) return;
      if (Date.now() - lastActivityAtRef.current > POLL_IDLE_MS) return;
      retrieveAndSetOnlinePeers();
      retrieveAndSetFriendList();
      // Pending requests are on the recovery path too: a missed push otherwise left the İstekler tab
      // stale until the drawer was reopened.
      retrieveAndSetPending();
      refreshActiveDuel();
    };
    const interval = setInterval(tick, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [
    retrieveAndSetOnlinePeers,
    retrieveAndSetFriendList,
    retrieveAndSetPending,
    refreshActiveDuel,
  ]);

  // Activity + visibility tracking for the poll gate above, and the catch-up refresh that makes
  // parking the tick invisible: coming back to the tab (or moving the mouse after a long pause)
  // refetches immediately instead of waiting out the interval.
  useEffect(() => {
    const noteActivityAndCatchUp = () => {
      const idleFor = Date.now() - lastActivityAtRef.current;
      lastActivityAtRef.current = Date.now();
      if (document.hidden) return;
      // Only refetch if the poll was actually parked; an ordinary click inside the drawer must not
      // turn every interaction into four requests.
      if (idleFor > POLL_IDLE_MS) {
        retrieveAndSetOnlinePeers();
        retrieveAndSetFriendList();
        retrieveAndSetPending();
        refreshActiveDuel();
      }
    };
    const onVisibility = () => {
      if (document.hidden) {
        hiddenSinceRef.current = Date.now();
        return;
      }
      const hiddenFor =
        hiddenSinceRef.current == null ? 0 : Date.now() - hiddenSinceRef.current;
      hiddenSinceRef.current = null;
      const idleFor = Date.now() - lastActivityAtRef.current;
      lastActivityAtRef.current = Date.now();
      // Catch up only when a tick was actually MISSED. The tick parks on two independent conditions,
      // so both are tested: away longer than one interval (hidden parking), or idle past the gate
      // (idle parking — which hidden time also accrues, since no activity event fires while away).
      // Testing idleness alone would skip the refetch for a tab hidden 20s, whose roster is stale;
      // testing nothing would spend four requests on every alt-tab round-trip, which is what this
      // gate exists to stop.
      if (hiddenFor < POLL_INTERVAL_MS && idleFor <= POLL_IDLE_MS) return;
      retrieveAndSetOnlinePeers();
      retrieveAndSetFriendList();
      retrieveAndSetPending();
      refreshActiveDuel();
    };
    // WHY this list and this phase, both load-bearing at a 2-minute threshold:
    //
    //  - `mousemove` and `touchmove` are in it because the panel's headline use case — open the
    //    drawer and watch for a friend to come online — involves no click at all. Without pointer
    //    movement counting, a learner sitting and reading is indistinguishable from an abandoned
    //    drawer, and the tick would park on a panel that is visible, foregrounded and being read.
    //  - `capture: true` because scroll events DO NOT BUBBLE. The roster has its own scrollport
    //    (`.dso-list`, overflow-y: auto), so a wheel-scroll fires there and never reaches a
    //    document-level bubble-phase listener; only the capture phase sees it on the way down.
    //
    // `useWebSocket`'s reaper uses the shorter bubble-phase list, and is right to: at its 30-minute
    // threshold the difference never bites. At 2 minutes it does.
    const events = [
      "mousedown",
      "mousemove",
      "keydown",
      "scroll",
      "click",
      "touchstart",
      "touchmove",
    ];
    events.forEach((event) =>
      document.addEventListener(event, noteActivityAndCatchUp, {
        passive: true,
        capture: true,
      }),
    );
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      events.forEach((event) =>
        // The phase is part of a listener's identity — removing without `capture` leaves every one
        // of these registered on an unmounted panel.
        document.removeEventListener(event, noteActivityAndCatchUp, {
          capture: true,
        }),
      );
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [
    retrieveAndSetOnlinePeers,
    retrieveAndSetFriendList,
    retrieveAndSetPending,
    refreshActiveDuel,
  ]);

  // Live friend events, rebroadcast by DuelSocialLayer. A status change patches the one row in place
  // rather than refetching the list; anything structural (accepted, removed) refetches.
  useEffect(() => {
    const handleFriendEvent = (event: Event) => {
      const payload = (event as CustomEvent).detail;
      if (!payload) return;
      if (payload.eventType === "STATUS_CHANGED") {
        setPeers((current) =>
          current.map((peer) =>
            peer.id === payload.subjectUserProfileId
              ? {
                  ...peer,
                  status: payload.status,
                  online: payload.status !== "OFFLINE",
                }
              : peer,
          ),
        );
        return;
      }
      retrieveAndSetOnlinePeers();
      retrieveAndSetFriendList();
      retrieveAndSetPending();
      // The add-tab search results carry a friendshipState frozen at search time, and nothing above
      // refetches them — so a request or acceptance from the other side left this learner's row on the
      // old button until a page reload re-ran the search. Patch the counterpart's row from the event.
      const nextState: Partial<FriendCandidate> | null =
        payload.eventType === "REQUEST_RECEIVED"
          ? { friendshipState: "PENDING_INCOMING", friendshipId: payload.friendshipId }
          : payload.eventType === "REQUEST_ACCEPTED"
            ? { friendshipState: "ACCEPTED", friendshipId: payload.friendshipId }
            : payload.eventType === "FRIEND_REMOVED"
              ? { friendshipState: "NONE", friendshipId: undefined }
              : null;
      if (nextState && payload.counterpartUserProfileId != null) {
        setCandidates((current) =>
          current.map((row) =>
            row.userProfileId === payload.counterpartUserProfileId
              ? { ...row, ...nextState }
              : row,
          ),
        );
      }
    };
    window.addEventListener(FRIEND_EVENT, handleFriendEvent);
    return () => window.removeEventListener(FRIEND_EVENT, handleFriendEvent);
  }, [
    retrieveAndSetOnlinePeers,
    retrieveAndSetFriendList,
    retrieveAndSetPending,
  ]);

  // Debounced server-side search — the add tab looks beyond the friend list, so it can't filter locally.
  const searchTimerRef = useRef<any>(null);
  // Which search the rendered candidates belong to. The debounce stops a request per keystroke but does NOT
  // serialize the requests: pause past the debounce, type again, and two are in flight — if the first is
  // slower its response lands last and overwrites the newer results with stale ones. Every path through this
  // effect bumps the counter, so a response that is no longer the latest is dropped instead of applied. That
  // also covers the cleared-term and tab-exit paths, where a late response would otherwise repopulate a list
  // the user just emptied.
  const searchSequenceRef = useRef(0);
  useEffect(() => {
    const sequence = ++searchSequenceRef.current;
    if (tab !== "ADD") {
      // Leaving the tab with a debounce still pending: drop the spinner flag here, or it survives until
      // this effect re-runs on return and the tab paints a search that isn't running.
      setSearching(false);
      return;
    }
    const term = searchName.trim();
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (term.length === 0) {
      setCandidates([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    searchTimerRef.current = setTimeout(async () => {
      const { data, err } = await searchFriendCandidates({ searchName: term });
      if (sequence !== searchSequenceRef.current) return;
      setSearching(false);
      setCandidates(!err && Array.isArray(data) ? (data as FriendCandidate[]) : []);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchName, tab]);

  const slotOpen = activeDuel && !activeDuel.player2UserProfileId;

  const handleChallengeSubmit = async (config: {
    roundCount: number;
    timeLimit: number;
    isPublic: boolean;
  }) => {
    if (challengePeerId == null || creating) return;
    setCreating(true);
    const { data, err } = await createDuel({
      level: userProfileGrade.level,
      roundCount: config.roundCount,
      timeLimit: config.timeLimit,
      isPublic: config.isPublic,
      userId: userInformation.id,
      userProfileId: userProfile.id,
      institutionId: userProfileSeason?.institutionDTO?.id,
      inviteeUserProfileId: challengePeerId,
    });
    setCreating(false);
    if (err) {
      NotificationManager.error(i18n.t(resolveDuelInviteRefusalKey(err, "could_not_send_challenge")));
      return;
    }
    setChallengePeerId(null);
    onClose();
    navigate(`/duels/${(data as any).id}`);
  };

  const handleInvite = async (
    peerProfileId: number,
    kind: "OPPONENT" | "SPECTATOR",
  ) => {
    if (!activeDuel) return;
    if (!beginRowPending(peerProfileId)) return;
    const { err } = await inviteToDuel(activeDuel.id, {
      inviteeUserProfileId: peerProfileId,
      kind,
    });
    endRowPending(peerProfileId);
    if (err) {
      NotificationManager.error(i18n.t(resolveDuelInviteRefusalKey(err, "could_not_send_invite")));
      return;
    }
    NotificationManager.success(i18n.t("invite_sent"));
  };

  const handleSendRequest = async (candidate: FriendCandidate) => {
    if (!beginRowPending(candidate.userProfileId)) return;
    const { err } = await sendFriendRequest({
      otherUserProfileId: candidate.userProfileId,
    });
    endRowPending(candidate.userProfileId);
    if (err) {
      NotificationManager.error(i18n.t("could_not_send_friend_request"));
      // Resync rather than trusting the failure: the row may exist anyway (a push that failed after the
      // commit, a request the other side sent us meanwhile), and a stale view here would leave the learner
      // retrying something that already succeeded.
      retrieveAndSetPending();
      return;
    }
    NotificationManager.success(i18n.t("friend_request_sent"));
    // Reflect the new state on the row without a second search round-trip.
    setCandidates((current) =>
      current.map((row) =>
        row.userProfileId === candidate.userProfileId
          ? { ...row, friendshipState: "PENDING_OUTGOING" }
          : row,
      ),
    );
    retrieveAndSetPending();
  };

  const handleAcceptRequest = async (candidate: FriendCandidate) => {
    if (!candidate.friendshipId) return;
    if (!beginRowPending(candidate.userProfileId)) return;
    const { err } = await acceptFriendRequest(candidate.friendshipId);
    endRowPending(candidate.userProfileId);
    if (err) {
      NotificationManager.error(i18n.t("could_not_answer_friend_request"));
      return;
    }
    NotificationManager.success(i18n.t("friend_request_accepted"));
    retrieveAndSetPending();
    retrieveAndSetFriendList();
    retrieveAndSetOnlinePeers();
    setCandidates((current) =>
      current.map((row) =>
        row.userProfileId === candidate.userProfileId
          ? { ...row, friendshipState: "ACCEPTED" }
          : row,
      ),
    );
  };

  const handleDeclineRequest = async (candidate: FriendCandidate) => {
    if (!candidate.friendshipId) return;
    if (!beginRowPending(candidate.userProfileId)) return;
    const { err } = await declineFriendRequest(candidate.friendshipId);
    endRowPending(candidate.userProfileId);
    if (err) {
      NotificationManager.error(i18n.t("could_not_answer_friend_request"));
      return;
    }
    retrieveAndSetPending();
    setCandidates((current) =>
      current.map((row) =>
        row.userProfileId === candidate.userProfileId
          ? { ...row, friendshipState: "NONE", friendshipId: undefined }
          : row,
      ),
    );
  };

  const handleCancelRequest = async (candidate: FriendCandidate) => {
    if (!candidate.friendshipId) return;
    if (!beginRowPending(candidate.userProfileId)) return;
    const { err } = await deleteFriendship(candidate.friendshipId);
    endRowPending(candidate.userProfileId);
    if (err) {
      NotificationManager.error(i18n.t("could_not_answer_friend_request"));
      return;
    }
    retrieveAndSetPending();
    setCandidates((current) =>
      current.map((row) =>
        row.userProfileId === candidate.userProfileId
          ? { ...row, friendshipState: "NONE", friendshipId: undefined }
          : row,
      ),
    );
  };

  const handleUnfriend = async () => {
    if (!unfriendPeer) return;
    // Carried on the row by the merge below, so it is always present for a rendered friend — a row only
    // exists because it came from the roster.
    const friendshipId = unfriendPeer.friendshipId;
    setUnfriendPeer(null);
    if (!friendshipId) {
      NotificationManager.error(i18n.t("could_not_remove_friend"));
      return;
    }
    const { err } = await deleteFriendship(friendshipId);
    if (err) {
      NotificationManager.error(i18n.t("could_not_remove_friend"));
      return;
    }
    NotificationManager.success(i18n.t("friend_removed"));
    retrieveAndSetFriendList();
    retrieveAndSetOnlinePeers();
  };

  // THE INTERSECTION. The roster is management's accepted-friend list; catchtivity's presence rows are
  // joined ONTO it by profile id, never the other way round. Consequences, both intended:
  //  - a presence row with no matching friendship is DROPPED, so a non-friend cannot be rendered and their
  //    live status cannot leak, even against a pre-#621 backend that still returns the whole season roster;
  //  - a friend with no presence row is KEPT as OFFLINE, which is also what a post-#621 backend reports
  //    for them, so the dimmed-row design behaves identically either way.
  //
  // Presence carries the display fields and the mascot inventory; the roster carries the friendship id and
  // is the fallback for name/grade/branch when a friend has no presence row at all.
  const friendRows = useMemo(() => {
    const presenceByProfileId = new Map<number, FriendPeer>();
    peers.forEach((peer) => {
      if (peer.id != null) presenceByProfileId.set(Number(peer.id), peer);
    });
    return friendList
      .filter((friend) => friend.userProfileId != null)
      .map((friend) => {
        const presence = presenceByProfileId.get(Number(friend.userProfileId));
        if (presence) {
          return {
            ...presence,
            friendshipId: friend.friendshipId,
          } as FriendPeer;
        }
        return {
          id: friend.userProfileId,
          userDTO: { firstName: friend.firstName, lastName: friend.lastName },
          branchDTO: {
            name: friend.branchName,
            gradeDTO: { name: friend.gradeName },
          },
          mascotInventory: [],
          online: false,
          status: "OFFLINE",
          friendshipId: friend.friendshipId,
        } as FriendPeer;
      });
  }, [friendList, peers]);

  // Online friends first, then by name — an offline friend is still listed, just not invitable.
  const sortedPeers = useMemo(() => {
    const collator = new Intl.Collator("tr");
    const displayName = (peer: FriendPeer) =>
      `${peer.userDTO?.firstName || ""} ${peer.userDTO?.lastName || ""}`.trim() ||
      peer.name ||
      "";
    return [...friendRows].sort((left, right) => {
      const leftOnline = left.online ? 0 : 1;
      const rightOnline = right.online ? 0 : 1;
      if (leftOnline !== rightOnline) return leftOnline - rightOnline;
      return collator.compare(displayName(left), displayName(right));
    });
  }, [friendRows]);

  const normalized = searchName.trim().toLocaleLowerCase("tr");
  const filteredPeers = sortedPeers.filter((peer) => {
    if (!normalized) return true;
    const first = peer?.userDTO?.firstName || "";
    const last = peer?.userDTO?.lastName || "";
    const name = peer?.name || "";
    return `${first} ${last} ${name}`
      .toLocaleLowerCase("tr")
      .includes(normalized);
  });

  const renderStatusChip = (peer: FriendPeer) => {
    const status: PresenceStatus = peer.online
      ? (peer.status as PresenceStatus) || "ONLINE"
      : "OFFLINE";
    const presentation = STATUS_PRESENTATION[status] || STATUS_PRESENTATION.ONLINE;
    return (
      <span
        className={`dso-chip dso-chip-${status.toLowerCase().replace("_", "-")}`}
      >
        <Icon name={presentation.icon} size={12} />
        {i18n.t(presentation.labelKey)}
      </span>
    );
  };

  const renderFriendRowAction = (peer: FriendPeer) => {
    const isPending = isRowPending(peer.id);
    // A friend who is offline, solving, in an exam or already duelling must not be pulled away. The
    // server enforces this too — this only spares the learner a pointless rejection.
    if (!isInterruptible(peer)) {
      return (
        <span className="dso-row-hint" title={i18n.t("friend_busy_tooltip")}>
          <PrimaryButton
            title={i18n.t("challenge")}
            size="small"
            disabled={true}
            onClick={() => {}}
          />
        </span>
      );
    }
    if (!activeDuel) {
      return (
        <PrimaryButton
          title={i18n.t("challenge")}
          size="small"
          // Hold the challenge until we've confirmed there's no existing duel — otherwise a fast
          // click during the initial fetch misroutes an invite through createDuel.
          disabled={isPending || !activeDuelResolved}
          onClick={() => setChallengePeerId(peer.id)}
        />
      );
    }
    if (slotOpen) {
      return (
        <PrimaryButton
          title={i18n.t("invite_to_duel")}
          size="small"
          disabled={isPending}
          onClick={() => handleInvite(peer.id, "OPPONENT")}
        />
      );
    }
    return (
      <SecondaryButton
        title={i18n.t("invite_to_watch")}
        size="small"
        disabled={isPending}
        onClick={() => handleInvite(peer.id, "SPECTATOR")}
      />
    );
  };

  const renderCandidateAction = (candidate: FriendCandidate) => {
    const isPending = isRowPending(candidate.userProfileId);
    if (candidate.friendshipState === "ACCEPTED") {
      return <span className="dso-row-note">{i18n.t("already_friends")}</span>;
    }
    if (candidate.friendshipState === "PENDING_OUTGOING") {
      return (
        <SecondaryButton
          title={i18n.t("cancel_friend_request")}
          size="small"
          disabled={isPending}
          onClick={() => handleCancelRequest(candidate)}
        />
      );
    }
    if (candidate.friendshipState === "PENDING_INCOMING") {
      // Both actions, same as the Requests tab: finding someone via search who has already asked you
      // shouldn't silently take away the ability to say no.
      return (
        <>
          <ApproveButton
            title={i18n.t("accept")}
            size="small"
            disabled={isPending}
            onClick={() => handleAcceptRequest(candidate)}
          />
          <CancelButton
            title={i18n.t("decline")}
            size="small"
            disabled={isPending}
            onClick={() => handleDeclineRequest(candidate)}
          />
        </>
      );
    }
    return (
      <PrimaryButton
        title={i18n.t("send_friend_request")}
        size="small"
        disabled={isPending}
        onClick={() => handleSendRequest(candidate)}
      />
    );
  };

  const renderCandidateRow = (
    candidate: FriendCandidate,
    actions: React.ReactNode,
  ) => {
    const subline = [candidate.gradeName, candidate.branchName]
      .filter(Boolean)
      .join(" · ");
    return (
      <div key={candidate.userProfileId} className="dso-row">
        <span className="dso-row-ava">
          <MascotWithItems inventoryItemDTOList={[]} />
        </span>
        <span className="dso-row-text">
          <span className="dso-row-name">
            {candidate.firstName} {candidate.lastName}
          </span>
          {subline ? <span className="dso-row-sub">{subline}</span> : null}
        </span>
        <div className="dso-row-actions">{actions}</div>
      </div>
    );
  };

  const renderFriendsTab = () => {
    // Both sources must have answered: the roster decides who is rendered and presence decides their status,
    // so painting after only presence lands would flash "no friends yet" at someone who has them.
    if (loading || !friendListResolved) {
      return (
        <div className="dso-state">
          <BaseLoading
            size="small"
            primaryColor="#57C2D3"
            secondaryColor="#57C2D3"
          />
        </div>
      );
    }
    if (filteredPeers.length === 0) {
      return (
        <div className="dso-empty">
          <EmptyState
            compact
            icon="users"
            title={i18n.t(
              friendRows.length === 0 ? "no_friends_yet" : "no_friends_found",
            )}
            description={
              friendRows.length === 0
                ? i18n.t("no_friends_yet_description")
                : undefined
            }
            action={
              friendRows.length === 0 ? (
                <PrimaryButton
                  title={i18n.t("add_friend")}
                  size="small"
                  onClick={() => {
                    setSearchName("");
                    setTab("ADD");
                  }}
                />
              ) : undefined
            }
          />
        </div>
      );
    }
    return filteredPeers.map((peer) => {
      const first = peer?.userDTO?.firstName || "";
      const last = peer?.userDTO?.lastName || "";
      const gradeName = peer?.branchDTO?.gradeDTO?.name || "";
      const branchName = peer?.branchDTO?.name || "";
      const subline = [gradeName, branchName].filter(Boolean).join(" · ");
      return (
        <div
          key={peer.id}
          className={`dso-row${peer.online ? "" : " dso-row-offline"}`}
        >
          <span className="dso-row-ava">
            <MascotWithItems inventoryItemDTOList={peer.mascotInventory || []} />
          </span>
          <span className="dso-row-text">
            <span className="dso-row-name">
              {first} {last}
            </span>
            {subline ? <span className="dso-row-sub">{subline}</span> : null}
            {renderStatusChip(peer)}
          </span>
          <div className="dso-row-actions">
            {renderFriendRowAction(peer)}
            <button
              type="button"
              className="dso-row-more"
              aria-label={i18n.t("remove_friend")}
              title={i18n.t("remove_friend")}
              onClick={() => setUnfriendPeer(peer)}
            >
              <Icon name="remove-red" size={14} />
            </button>
          </div>
        </div>
      );
    });
  };

  const renderRequestsTab = () => {
    const { incomingList, outgoingList } = pending;
    if (incomingList.length === 0 && outgoingList.length === 0) {
      return (
        <div className="dso-empty">
          <EmptyState
            compact
            icon="users"
            title={i18n.t("no_friend_requests")}
          />
        </div>
      );
    }
    return (
      <>
        {incomingList.length > 0 ? (
          <>
            <div className="dso-sec-label">{i18n.t("incoming_requests")}</div>
            {incomingList.map((candidate) =>
              renderCandidateRow(
                candidate,
                <>
                  <ApproveButton
                    title={i18n.t("accept")}
                    size="small"
                    disabled={isRowPending(candidate.userProfileId)}
                    onClick={() => handleAcceptRequest(candidate)}
                  />
                  <CancelButton
                    title={i18n.t("decline")}
                    size="small"
                    disabled={isRowPending(candidate.userProfileId)}
                    onClick={() => handleDeclineRequest(candidate)}
                  />
                </>,
              ),
            )}
          </>
        ) : null}
        {outgoingList.length > 0 ? (
          <>
            <div className="dso-sec-label">{i18n.t("outgoing_requests")}</div>
            {outgoingList.map((candidate) =>
              renderCandidateRow(
                candidate,
                <SecondaryButton
                  title={i18n.t("cancel_friend_request")}
                  size="small"
                  disabled={isRowPending(candidate.userProfileId)}
                  onClick={() => handleCancelRequest(candidate)}
                />,
              ),
            )}
          </>
        ) : null}
      </>
    );
  };

  const renderAddTab = () => {
    if (searching) {
      return (
        <div className="dso-state">
          <BaseLoading
            size="small"
            primaryColor="#57C2D3"
            secondaryColor="#57C2D3"
          />
        </div>
      );
    }
    if (searchName.trim().length === 0) {
      return (
        <div className="dso-empty">
          <EmptyState
            compact
            icon="magnifyingGlass"
            title={i18n.t("search_friend_prompt")}
          />
        </div>
      );
    }
    if (candidates.length === 0) {
      return (
        <div className="dso-empty">
          <EmptyState compact icon="users" title={i18n.t("no_friends_found")} />
        </div>
      );
    }
    return candidates.map((candidate) =>
      renderCandidateRow(candidate, renderCandidateAction(candidate)),
    );
  };

  const incomingCount = pending.incomingList.length;

  return (
    <div
      className="dso-overlay"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <style>{duelSocialStyles + friendPanelStyles}</style>
      <div className="dso-drawer" role="dialog" aria-modal="true">
        <div className="dso-head">
          <span className="dso-title">{i18n.t("friends_online")}</span>
          <button
            type="button"
            className="dso-close"
            aria-label={i18n.t("close")}
            onClick={onClose}
          >
            <Icon name="close" size={16} />
          </button>
        </div>

        <div className="dso-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "FRIENDS"}
            className={`dso-tab${tab === "FRIENDS" ? " dso-tab-on" : ""}`}
            onClick={() => setTab("FRIENDS")}
          >
            {i18n.t("my_friends")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "REQUESTS"}
            className={`dso-tab${tab === "REQUESTS" ? " dso-tab-on" : ""}`}
            onClick={() => setTab("REQUESTS")}
          >
            {i18n.t("friend_requests")}
            {incomingCount > 0 ? (
              <span className="dso-tab-badge">{incomingCount}</span>
            ) : null}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "ADD"}
            className={`dso-tab${tab === "ADD" ? " dso-tab-on" : ""}`}
            onClick={() => setTab("ADD")}
          >
            {i18n.t("add_friend")}
          </button>
        </div>

        {tab !== "REQUESTS" ? (
          <div className="dso-toolbar">
            <div className="dso-search">
              <Icon name="magnifyingGlass" size={14} />
              <input
                type="text"
                value={searchName}
                placeholder={
                  tab === "ADD"
                    ? i18n.t("search_season_learner_placeholder")
                    : i18n.t("search_friend_placeholder")
                }
                aria-label={i18n.t("find_friends")}
                onChange={(event) => setSearchName(event.target.value)}
              />
            </div>
            {tab === "FRIENDS" ? (
              <button
                type="button"
                className="dso-refresh"
                aria-label={i18n.t("refresh")}
                onClick={() => {
                  setLoading(true);
                  retrieveAndSetOnlinePeers();
                  retrieveAndSetFriendList();
                  refreshActiveDuel();
                }}
              >
                <Icon name="refresh" size={14} />
              </button>
            ) : null}
          </div>
        ) : null}

        <div className="dso-list no-scrollbar">
          {tab === "FRIENDS"
            ? renderFriendsTab()
            : tab === "REQUESTS"
            ? renderRequestsTab()
            : renderAddTab()}
        </div>
      </div>

      <BaseModal
        title={i18n.t("create_duel_game")}
        icon="🎮"
        isOpen={challengePeerId != null}
        onRequestClose={() => setChallengePeerId(null)}
        size="medium"
      >
        <DuelCreateForm
          initialIsPublic={true}
          submitLabel={creating ? i18n.t("creating") : i18n.t("create_lobby")}
          submitting={creating}
          onSubmit={handleChallengeSubmit}
        />
      </BaseModal>

      <BaseModal
        title={i18n.t("remove_friend")}
        isOpen={unfriendPeer != null}
        onRequestClose={() => setUnfriendPeer(null)}
        size="small"
      >
        <div className="dso-confirm">
          <p className="dso-confirm-text">
            {i18n.t("remove_friend_confirmation")}
          </p>
          <div className="dso-confirm-actions">
            <SecondaryButton
              title={i18n.t("cancel")}
              size="medium"
              onClick={() => setUnfriendPeer(null)}
            />
            <CancelButton
              title={i18n.t("remove_friend")}
              size="medium"
              onClick={handleUnfriend}
            />
          </div>
        </div>
      </BaseModal>
    </div>
  );
};

export default FriendsOnlinePanel;
